import type { Cursor, ID } from '#shared'
import { infiniteQueryOptions } from '@tanstack/vue-query'
import { computed, unref, type MaybeRef } from 'vue'
import { getMessages, type GetMessagesOptions } from '../api/get-messages'

export interface MessagesQueryParams {
  search?: string | null
  startAfter?: string | null
}

interface MessagesQueryPageParam {
  direction: 'before' | 'after'
  cursor: Cursor | null
}

export const getMessagesQueryKey = (chatId: MaybeRef<ID>, params: MaybeRef<MessagesQueryParams> = {}) =>
  [
    'messages' as const,
    chatId,
    computed(() => unref(params).search || null) as MaybeRef<string | null>,
    computed(() => unref(params).startAfter || null),
  ] as const

export const getMessagesQueryKeyPrefixByChat = (chatId: MaybeRef<ID>) => ['messages', chatId] as const

export const getMessagesQueryKeyGlobalPrefix = () => ['messages'] as const

export const getMessagesQueryOptions = (chatId: MaybeRef<ID>, params: MaybeRef<MessagesQueryParams> = {}) =>
  infiniteQueryOptions({
    queryKey: getMessagesQueryKey(chatId, params),

    queryFn: async ({ pageParam }) => {
      const options: GetMessagesOptions = { search: unref(params).search }
      if (pageParam) options[pageParam.direction] = pageParam.cursor
      return getMessages(unref(chatId), options)
    },

    getNextPageParam: lastPage =>
      lastPage.hasNextPage
        ? { direction: 'after' as const, cursor: lastPage.endCursor?.toString() ?? null }
        : undefined,

    getPreviousPageParam: firstPage =>
      firstPage.hasPrevPage
        ? { direction: 'before' as const, cursor: firstPage.startCursor?.toString() ?? null }
        : undefined,

    initialPageParam: computed(() => ({
      direction: 'after' as const,
      cursor: unref(params).startAfter ?? undefined,
    })) as MaybeRef<MessagesQueryPageParam | undefined>,

    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: 60_000,
  })
